import type { ManagedUser, Permission } from "@brasilnovo/shared";
import { effectivePermissions, normalizePermissions } from "@brasilnovo/shared";

// Lógica pura da tela "Usuários e permissões" (D-27), separada para teste.

export interface Ajustes {
  grant: Permission[];
  revoke: Permission[];
}

/**
 * Marca/desmarca uma permissão para UMA pessoa. A tela mostra checkboxes do
 * que a pessoa pode fazer; aqui isso vira o ajuste relativo ao grupo:
 *   · coincide com o grupo → nenhum ajuste (o grupo volta a mandar);
 *   · grupo não tem e marcou → concessão individual;
 *   · grupo tem e desmarcou → retirada individual.
 */
export function alternarPermissao(
  grupo: readonly Permission[],
  ajustes: Ajustes,
  perm: Permission,
  marcado: boolean,
): Ajustes {
  const grant = ajustes.grant.filter((p) => p !== perm);
  const revoke = ajustes.revoke.filter((p) => p !== perm);
  const doGrupo = grupo.includes(perm);
  if (marcado && !doGrupo) grant.push(perm);
  if (!marcado && doGrupo) revoke.push(perm);
  return { grant: normalizePermissions(grant), revoke: normalizePermissions(revoke) };
}

/** Trocar de grupo descarta ajustes que ficaram redundantes com o novo grupo. */
export function limparAjustes(grupo: readonly Permission[], ajustes: Ajustes): Ajustes {
  return {
    grant: ajustes.grant.filter((p) => !grupo.includes(p)),
    revoke: ajustes.revoke.filter((p) => grupo.includes(p)),
  };
}

export function permissoesEfetivas(grupo: readonly Permission[] | null, ajustes: Ajustes): Permission[] {
  // Sem grupo, a pessoa fica só com o que for concedido individualmente.
  return effectivePermissions({ role: "motorista", groupPermissions: grupo ?? [], ...ajustes });
}

export function linkConvite(origin: string, token: string): string {
  return `${origin.replace(/\/$/, "")}/convite/${token}`;
}

/** Mensagem pronta para enviar NO PRIVADO (WhatsApp/e-mail). */
export function mensagemConvite(nome: string, link: string, expiraEm: string): string {
  const primeiro = nome.trim().split(/\s+/)[0] ?? "";
  const ate = new Date(expiraEm).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
  return [
    `Olá, ${primeiro}! Seu acesso ao Controle Operacional da Brasil Novo está pronto.`,
    `Abra o link abaixo e defina a sua senha (vale uma única vez, até ${ate}):`,
    link,
    "Não repasse este link: quem abrir primeiro define a senha da conta.",
  ].join("\n");
}

export const whatsappUrl = (texto: string) => `https://wa.me/?text=${encodeURIComponent(texto)}`;

export type Situacao = "ativo" | "convite" | "aguardando" | "inativo";

/** Situação da conta para a tabela. */
export function situacao(u: Pick<ManagedUser, "active" | "invitePending" | "lastLoginAt">): Situacao {
  if (!u.active) return "inativo";
  if (u.invitePending) return "convite";
  if (!u.lastLoginAt) return "aguardando";
  return "ativo";
}

export const SITUACAO_LABEL: Record<Situacao, string> = {
  ativo: "Ativo",
  convite: "Convite enviado",
  aguardando: "Nunca entrou",
  inativo: "Inativo",
};
